import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { submitForm } from "@/api/apis";
interface CreatorLeadFormProps {
  onClose: () => void;
}

export default function CreatorLeadForm({ onClose }: CreatorLeadFormProps) {
  const [, setLocation] = useLocation();
  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    artistName: "",
    musicGenre: "",
    roleType: "Songwriter",
    hasPublishedMusic: false,
    consent: false,
    socialmedia: {},
  });

  const SOCIAL_MEDIA_PLATS = [
    "Facebook",
    "Instagram",
    "X",
    "Youtube",
    "Tiktok",
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) =>
    setData({
      ...data,
      [e.target.name]:
        e.target.type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : e.target.value,
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    /* ---------- Email Form Submission ---------- */
    const emailData = {
      to: "contact@wearemusicnation.ae",
      subject: "New Creator Registration",
      body: `Name: ${data.name}\nEmail: ${data.email}\nPhone: ${
        data.phone
      }\nArtist/Band Name: ${data.artistName}\nMusic Genre: ${
        data.musicGenre
      }\nRole: ${data.roleType}\nHas Published Music: ${
        data.hasPublishedMusic ? "Yes" : "No"
      }`,
    };

    // Handle form submission to contact@wearemusicnation.ae
    //console.log("Creator form sent to:", emailData.to);

    //console.log("Creator lead captured:", data);

    const API_KEYS: any = {
      name: "full_name",
      phone: "phone_number",
      artistName: "artist_name",
      musicGenre: "primary_genre",
      roleType: "role",
      hasPublishedMusic: "has_published",
      consent: "agree_contact",
      socialmedia: "social_media"
    };

    const formData: any = data;

    Object.keys(data).map((key: string) => {
      formData[API_KEYS[key] || key] = formData[key];
      if (API_KEYS[key]) {
        delete formData[key];
      }
    });

    submitForm("CreatorSubmission", formData)
      .then((res) => {
        onClose();

        setTimeout(() => {
          setLocation("/thank-you-creator");
        }, 100);
      })
      .catch((e) => {
        console.log("SOMETHING WENT WRONG", e.response);
      });
  };

  return (
    <form
      name="creator-lead"
      data-netlify="true"
      netlify-honeypot="bot-field"
      onSubmit={handleSubmit}
      className="space-y-6 p-8 bg-musicBlack rounded-lg max-w-lg mx-auto text-musicWhite"
    >
      <input type="hidden" name="bot-field" />
      <input type="hidden" name="form-name" value="creator-lead" />

      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Creator Registration</h2>
        <p className="text-musicGrey">Join Music Nation as a Creator</p>
      </div>

      {/* Full Name */}
      <div>
        <label className="block text-sm mb-1">Full Name *</label>
        <input
          name="name"
          type="text"
          required
          placeholder="Your full name"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3"
          value={data.name}
          onChange={handleChange}
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm mb-1">Email Address *</label>
        <input
          name="email"
          type="email"
          required
          placeholder="your@email.com"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3"
          value={data.email}
          onChange={handleChange}
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm mb-1">Phone Number</label>
        <input
          name="phone"
          type="tel"
          placeholder="+971 XX XXX XXXX"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3"
          value={data.phone}
          onChange={handleChange}
        />
      </div>

      {/* Artist/Band Name */}
      <div>
        <label className="block text-sm mb-1">Artist/Band Name</label>
        <input
          name="artistName"
          type="text"
          placeholder="Your stage or band name"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3"
          value={data.artistName}
          onChange={handleChange}
        />
      </div>

      {/* Music Genre */}
      <div>
        <label className="block text-sm mb-1">Primary Music Genre</label>
        <select
          name="musicGenre"
          className="w-full bg-black border border-musicGrey/30 rounded-md p-3"
          value={data.musicGenre}
          onChange={handleChange}
        >
          <option value="">Select genre</option>
          <option value="Pop">Pop</option>
          <option value="Rock">Rock</option>
          <option value="Hip Hop">Hip Hop</option>
          <option value="Electronic">Electronic</option>
          <option value="R&B">R&B</option>
          <option value="Jazz">Jazz</option>
          <option value="Classical">Classical</option>
          <option value="World Music">World Music</option>
          <option value="Folk">Folk</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Role Type */}
      <div>
        <label className="block text-sm mb-1">I am a... *</label>
        {[
          "Songwriter",
          "Composer",
          "Publisher",
          "Rights holder/other",
          "Featured Artist",
          "Producer",
          "Record Label",
        ].map((op, i) => {
          return (
            <label key={i} className="flex items-center space-x-2 text-sm mb-2">
              <input
                type="checkbox"
                name="hasPublishedMusic"
                checked={data.roleType?.includes(op)}
                onChange={() => {
                  if (data.roleType?.includes(op)) {
                    setData({
                      ...data,
                      roleType: data.roleType
                        .split(",")
                        .filter((e) => e != op)
                        .join(","),
                    });
                  } else {
                    setData({
                      ...data,
                      roleType: [...(data?.roleType?.split(",") || []), op].join(","),
                    });
                  }
                }}
              />
              <span>{op}</span>
            </label>
          );
        })}
      </div>

      {SOCIAL_MEDIA_PLATS.map((s: any) => {
        const socialMedia: any = data?.socialmedia;
        return (
          <div key={s}>
            <label className="block text-sm mb-1">{s}</label>
            <input
              type="text"
              placeholder="Enter your handle"
              className="w-full bg-black border border-musicGrey/30 rounded-md p-3"
              value={socialMedia?.[s] || ""}
              onChange={(e) => {
                setData({
                  ...data,
                  socialmedia: { ...data.socialmedia, [s]: e.target.value },
                });
              }}
            />
          </div>
        );
      })}

      {/* Published Music */}
      <label className="flex items-center space-x-2 text-sm">
        <input
          type="checkbox"
          name="hasPublishedMusic"
          checked={data.hasPublishedMusic}
          onChange={handleChange}
        />
        <span>I have published music commercially</span>
      </label>

      {/* Consent */}
      <label className="flex items-center space-x-2 text-sm">
        <input
          type="checkbox"
          name="consent"
          checked={data.consent}
          onChange={handleChange}
          required
        />
        <span>
          I agree to be contacted by Music Nation regarding creator services.
        </span>
      </label>

      <Button type="submit" className="cta-button w-full">
        Submit Creator Registration
      </Button>
    </form>
  );
}
